<template>
  <div class="menu-position">
    <!-- Horizontal icon strip -->
    <div class="tools-strip">
      <!-- <button
        v-if="globalIsUserAllowed('Zoom_Widget') && !analyticsVisible && !wclVisible"
        class="tool-btn"
        :class="{ 'tool-btn--active': activePanel === 'zoom' }"
        title="Zoom Level"
        @click="togglePanel('zoom')"
      >
        <fa :icon="['fas', 'search']" size="sm" />
      </button> -->

      <!-- <button
        v-if="globalIsUserAllowed('Lat_Long_Widget')"
        class="tool-btn"
        :class="{ 'tool-btn--active': activePanel === 'coordinates' }"
        title="Coordinates"
        @click="togglePanel('coordinates')"
      >
        <fa :icon="['fas', 'crosshairs']" size="sm" />
      </button> -->

      <button
        v-if="globalIsUserAllowed('Change_Extent_Widget')"
        class="tool-btn"
        :class="{ 'tool-btn--active': activePanel === 'extents' }"
        title="Change Extent"
        @click="togglePanel('extents')"
      >
        <fa :icon="['fas', 'globe-americas']" size="sm" />
      </button>

      <button
        v-if="globalIsUserAllowed('Base_Map_Widget')"
        class="tool-btn"
        :class="{ 'tool-btn--active': activePanel === 'base' }"
        title="Base Maps"
        @click="togglePanel('base')"
      >
        <fa :icon="['fas', 'map']" size="sm" />
      </button>

      <button
        v-if="globalIsUserAllowed('2D_Lidar') && (ansiComponent || drtComponents || analyticsVisible || wclVisible)"
        class="tool-btn"
        :class="{ 'tool-btn--active': showLidarPanel, 'tool-btn--warn': !lidarMinZoomAchieved }"
        title="2D Lidar"
        @click="toggleLidar"
      >
        <fa :icon="['fas', lidarMinZoomAchieved ? 'layer-group' : 'exclamation-triangle']" size="sm" />
      </button>

      <button
        v-if="globalIsUserAllowed('Measure_Widget') && (ansiComponent || drtComponents || analyticsVisible || wclVisible)"
        class="tool-btn"
        :class="{ 'tool-btn--active': measuringToolOn }"
        title="Measuring Tool"
        @click="toggleMeasure"
      >
        <fa :icon="['fas', 'ruler']" size="sm" />
      </button>
    </div>

    <!-- Popup panels — always mounted via v-show so parent refs remain valid -->
    <div class="tools-popup-area">
      <div class="tools-popup" v-show="activePanel === 'zoom'">
        <MMP_ExpandableDisplay
          ref="updateZoom"
          :collapsedComponent="'zoom'"
          :map="map"
          :zoomLevel="zoomLevel"
          @zoomLevelChanged="$emit('zoomLevelChanged', $event)"
        />
      </div>

      <div class="tools-popup" v-show="activePanel === 'coordinates'">
        <MMP_ExpandableDisplay
          ref="updateClickCoords"
          :collapsedComponent="'coordinates'"
          :map="map"
          :pointVectorSource="pointVectorSource"
          :ansiComponent="ansiComponent"
          :drtComponents="drtComponents"
          :showToggle="showToggle"
          @addPoint="$emit('addPoint', $event)"
          @showToggle="$emit('showToggle')"
          @getVectorlayer="$emit('getVectorlayer', $event)"
          @toggleChange="$emit('toggleChange', $event)"
        />
      </div>

      <div class="tools-popup" v-show="activePanel === 'extents'">
        <MMP_ExpandableDisplay
          ref="updateExtent"
          :collapsedComponent="'extents'"
          :homeScreenComponents="homeScreenComponents"
          :map="map"
          :selectedExtent="selectedExtent"
          :userOrg="user.org"
          :baseMapsOpen="baseMapsOpen"
          :analyticsVisible="analyticsVisible"
          @extentsExpandChange="$emit('extentsExpandChange', $event)"
          @extentChanged="$emit('extentChanged', $event)"
        />
      </div>

      <div class="tools-popup" v-show="activePanel === 'base'">
        <MMP_ExpandableDisplay
          ref="updateBase"
          :collapsedComponent="'base'"
          :currentBaseMap="currentBaseMap"
          :map="map"
          :isAnsiComponent="ansiComponent"
          :extentsOpen="extentsOpen"
          :showLidarPanel="showLidarPanel"
          @baseMapsExpandChange="$emit('baseMapsExpandChange', $event)"
          @toggleBaseLayer="$emit('toggleBaseLayer', $event)"
        />
      </div>

      <!-- Measurement panel — Lidar and Measure are the one pair of widgets
           allowed to stay open together (everything else remains mutually
           exclusive). Measure is placed first so it renders above Lidar
           when both are visible, per design. -->
      <div class="tools-popup" v-show="measuringToolOn">
        <MMP_Measurement
          :showMeasuringTool="measuringToolOn"
          :measurement="measurement"
        />
      </div>

      <!-- Lidar panel — shown independently when lidar is toggled on -->
      <div class="lidar-popup" v-show="showLidarPanel">
        <MMP_LidarTiles
          :showLidarPanel="showLidarPanel"
          :lidarMinZoomAchieved="lidarMinZoomAchieved"
          :distinct_lidar="distinct_lidar"
          :lidarIsLoading="lidarIsLoading"
          :map="map"
          :selectedExtent="selectedExtent"
          :zoomLevel="zoomLevel"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import WKT from 'ol/format/WKT';
import CircleStyle from 'ol/style/Circle'
import { Draw, Snap } from 'ol/interaction'
import Fill from 'ol/style/Fill'
import LineString from 'ol/geom/LineString'
import Polygon from 'ol/geom/Polygon'
import Stroke from 'ol/style/Stroke'
import Style from 'ol/style/Style'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { ref } from 'vue'
import {getLength, getArea, getDistance} from 'ol/sphere'
import axios from 'axios'
import JSZip from 'jszip'
import KML from 'ol/format/KML'
import GeometryCollection from 'ol/geom/GeometryCollection'
import MultiPolygon from 'ol/geom/MultiPolygon'
import {
  get as projGet,
  fromLonLat,
  toLonLat,
  transform,
  transformExtent
} from 'ol/proj'

import MMP_ExpandableDisplay from './MMP_ExpandableDisplay.vue';
import MMP_LidarTiles from './MMP_LidarTiles.vue';
import MMP_Measurement from './MMP_Measurement.vue';

export default {
  name: 'MMP_Widgets',
  components: {
    MMP_ExpandableDisplay,
    MMP_LidarTiles,
    MMP_Measurement
  },
  props: [
    'map',
    'zoomLevel',
    'pointVectorSource',
    'ansiComponent',
    'drtComponents',
    'analyticsVisible',
    'wclVisible',
    'showToggle',
    'user',
    'selectedExtent',
    'homeScreenComponents',
    'baseMapsOpen',
    'currentBaseMap',
    'extentsOpen',
    'showLidarPanel',
    'lidarMinZoomAchieved',
    'showMeasuringTool',
    'distinct_lidar',
    'extentName',
    'lidarIsLoading'
  ],
  computed: {
    ...mapGetters(['globalUserRole', 'globalAllowedUserModules', 'globalIsUserAllowed'])
  },
  data() {
    return {
      activePanel: null,
      draw: null,
      measurement: ['0', 'm'],
      measuringToolOn: false
    };
  },
  methods: {
    togglePanel(name) {
      // Extents/Base/etc. stay mutually exclusive with everything, including
      // Lidar and Measure.
      if (this.showLidarPanel) this.$emit('lidarToggleChange');
      if (this.measuringToolOn) this.stopMeasuring();
      this.activePanel = this.activePanel === name ? null : name;
    },
    toggleLidar() {
      // Lidar still closes the Extents/Base/etc. group, but Lidar and
      // Measure are allowed to be open at the same time (see template order
      // above for the stacking).
      this.activePanel = null;
      this.$emit('lidarToggleChange');
    },
    addInteraction() {
      var sketch
      var source = new VectorSource()
      var vector = new VectorLayer({
        source: source,
        zIndex: 200,
        style: new Style({
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)'
          }),
          stroke: new Stroke({
            color: '#ffcc33',
            width: 2
          }),
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({
              color: '#ffcc33'
            })
          })
        })
      })
      var type = 'LineString'
      this.draw = new Draw({
        source: source,
        type: type,
        style: new Style({
          fill: new Fill({
            color: 'rgba(255, 2, 2, 0.2)'
          }),
          stroke: new Stroke({
            color: 'rgba(255, 2, 2, 0.5)',
            lineDash: [10, 10],
            width: 2
          }),
          image: new CircleStyle({
            radius: 5,
            stroke: new Stroke({
              color: 'rgba(255, 2, 2, 0.7)'
            }),
            fill: new Fill({
              color: 'rgba(255, 2, 2, 0.2)'
            })
          })
        })
      })
      this.map.addInteraction(this.draw)
      const self = this
      this.draw.on('drawstart', function (evt) {
        // Set sketch
        sketch = evt.feature
        source.clear()
        let listener = sketch.getGeometry().on('change', function (evt) {
          var geom = evt.target
          var output
          if (geom instanceof Polygon) {
            output = self.formatArea(geom) // REFACTOR SUGGESTION -- formatArea is not a fn...?
          } else if (geom instanceof LineString) {
            output = self.formatLength(geom)
          }

          // output[0] is m/km, output[1] is ft/mi
          self.measurement = output[0].split(' ')
          self.$emit('measurement', self.measurement)
        })
      })

      this.draw.on('drawend', function () {
        sketch = null
      })
    },
    formatLength(line) {
      const coordinates = line.getCoordinates();
      let totalLength = 0;

      for (let i = 0; i < coordinates.length - 1; i++) {
        const c1 = transform(coordinates[i], 'EPSG:3857', 'EPSG:4326');
        const c2 = transform(coordinates[i + 1], 'EPSG:3857', 'EPSG:4326');
        totalLength += getDistance(c1, c2);
      }

      var length = getLength(line)
      var output
      var meters
      var feet
      if (length > 100) {
        meters = `${Math.round((length / 1000) * 100) / 100} km`
        feet = `${Math.round((length / 1609.344) * 100) / 100} miles`
      } else {
        meters = `${Math.round(length * 100) / 100} m`
        feet = `${Math.round(length * 3.281 * 100) / 100} feet`
      }
      output = [meters, feet]
      return output
    },
    async toggleMeasure() {
      if (this.measuringToolOn) {
        this.stopMeasuring()
        return
      }
      // Measure still closes the Extents/Base/etc. group, but leaves Lidar
      // alone — they're allowed to be open together.
      this.activePanel = null
      this.measuringToolOn = true
      this.$emit('toggleMeasuringTool')
      this.addInteraction()
      this.$emit('measurement', this.measurement)
    },
    stopMeasuring() {
      this.measuringToolOn = false
      this.$emit('toggleMeasuringTool')
      if (this.draw) this.map.removeInteraction(this.draw)
      this.measurement = ['0', 'm']
      // The Draw interaction leaves a crosshair cursor on the viewport;
      // clear it right away so the cursor returns to normal on toggle-off.
      const viewport = this.map.getViewport()
      if (viewport) viewport.style.cursor = ''
    },
  },
  watch: {
    activePanel(newPanel) {
      if (!newPanel) return;
      const refMap = {
        zoom: 'updateZoom',
        coordinates: 'updateClickCoords',
        extents: 'updateExtent',
        base: 'updateBase'
      };
      const refName = refMap[newPanel];
      this.$nextTick(() => {
        if (refName && this.$refs[refName] && !this.$refs[refName].expand) {
          this.$refs[refName].expansionToggle();
        }
      });
    }
  }
};
</script>

<style scoped>
.menu-position {
  position: relative;
}

.tools-strip {
  display: flex;
  flex-direction: row;
  background: #063c6d;
  border-radius: 0.25rem;
  box-shadow: 0 2px 8px rgba(6, 60, 109, 0.25);
  overflow: hidden;
}

.tool-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 40px;
  background: none;
  border: none;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  flex-shrink: 0;
}

.tool-btn:last-child {
  border-right: none;
}

.tool-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.tool-btn--active {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.3);
}

.tool-btn--warn {
  color: #ffc107;
}

/* Popup panel area — attached directly under the strip so it reads as one unit */
.tools-popup-area {
  position: absolute;
  top: calc(100% + 5px);
  right: 0;
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
}

.tools-popup {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: #063c6d;
  /* border: 1px solid rgba(255, 255, 255, 0.12); */
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(6, 60, 109, 0.45);
  overflow: hidden;
  transform-origin: top center;
  animation: toolsDropDown 0.18s ease-out both;
}

/* Gentle slide-down reveal — plays each time a panel is shown (v-show flip) */
@keyframes toolsDropDown {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Reset the inner ExpandableDisplay's absolute positioning so
   content renders inside the popup instead of anchoring to page edges */
.tools-popup ::v-deep .blue-box-collapsed,
.tools-popup ::v-deep .blue-box-expanded {
  position: relative !important;
  right: auto !important;
}

/* Vertical list of compact cards inside the dark popup */
.tools-popup ::v-deep .basemap-panel,
.tools-popup ::v-deep .extents-panel {
  width: 200px !important;
  min-width: 200px !important;
  padding: 8px 10px 10px !important;
}
.tools-popup ::v-deep .basemap-row,
.tools-popup ::v-deep .extents-row {
  flex-direction: column !important;
  flex-wrap: nowrap !important;
  gap: 5px !important;
  overflow: visible !important;
  max-height: none !important;
}
.tools-popup ::v-deep .basemap-card,
.tools-popup ::v-deep .extent-card {
  flex-direction: row !important;
  align-items: center !important;
  gap: 8px !important;
  width: 100% !important;
  padding: 5px 7px !important;
  border-radius: 6px !important;
}
.tools-popup ::v-deep .basemap-img,
.tools-popup ::v-deep .extent-img {
  width: 48px !important;
  height: 34px !important;
  object-fit: contain !important;
  border-radius: 3px !important;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.06);
}
.tools-popup ::v-deep .basemap-name,
.tools-popup ::v-deep .extent-name {
  text-align: left !important;
  font-size: 0.75rem !important;
  color: rgba(255, 255, 255, 0.9) !important;
  white-space: normal !important;
  word-break: break-word !important;
  line-height: 1.25 !important;
}
.tools-popup ::v-deep .basemap-label,
.tools-popup ::v-deep .extents-label {
  color: rgba(255, 255, 255, 0.45) !important;
  font-size: 0.6rem !important;
}
</style>